/* ELC Shift — Admin Accounts & Areas module (manager-account CRUD, area CRUD, area/account helpers).
   Extracted from admin.html block #12 (Sprint 5) via AST. Behaviour-preserving: every function was a
   global inline declaration, so all are re-exposed on window. Bundle loads BEFORE block #12 (block #12
   calls getAreaStoreMap()/loadAccounts() at top level) and before the reviews IIFE. Reads admin globals
   (DB, ALL_ACCOUNTS, ALL_AREAS, staffData, currentUser…) at runtime. */
import { askSecret } from '../shared/ask-secret.js';   // v133.120: passwords never in a prompt() box
import { setHTML } from '../shared/set-html.js';   // v136.8: null-safe innerHTML for admin renders
(function(){

// ── Per-person admin tab visibility (v133) ──────────────────────────────────
// Head Office can show/hide individual top-level admin tabs per account, layered
// on the role defaults. Stored as a { tabId: bool } override map on the account;
// a tab absent from the map follows the role default.
var TAB_CONTROL = [
  { id: 'dashboard',    label: '📊 Dashboard' },
  { id: 'ambrief',      label: '☀️ Brief' },
  { id: 'people',       label: '👥 People' },
  { id: 'scheduling',   label: '🗓 Scheduling' },
  { id: 'operations',   label: '✅ Operations' },
  { id: 'targets',      label: '🎯 Targets' },
  { id: 'areakpis',     label: '📈 Area KPIs' },
  { id: 'top50',        label: '📦 Top 50' },
  { id: 'scan',         label: '📷 Scan' },   // v136.19 - on for every role by default (data uploads stay Head Office only)
  { id: 'stores',       label: '🏪 Stores' },
  { id: 'store-visits', label: '📋 Store Visits' },
  { id: 'amplanner',    label: '📅 AM Planner' },
  { id: 'covercheck',   label: '🕐 Cover check' },   // v132.97
  { id: 'storerotas',   label: '🗓 Store rotas' },   // v136.3 - read only; on for every role by default
  { id: 'storeoverview', label: '🏬 Store overview' },   // on by default; the server answers Head Office and area managers only
  { id: 'scorecard',    label: '💯 Scorecard' },   // v136.41; v136.48: Head Office everything, area managers their own area (the server checks)
  { id: 'cxfeedback',   label: '⭐ Customer' },
  { id: 'pettycash',    label: '💵 Petty Cash' },
  { id: 'policies',     label: '📖 Policies' },
  { id: 'concerns',     label: '🔒 Concerns' },
  { id: 'audit',        label: '🛡 Audit' },
  { id: 'emailhub',     label: '📧 Auto emails' },   // v131.26 — the automated-emails hub
  { id: 'jack',         label: '💬 JACK' },          // v132.70 - Head Office JACK chat
];
// Role defaults: Head Office sees everything; everyone else sees all EXCEPT the
// whistleblowing Concerns tab (a concern may be ABOUT an area manager).
function tabRoleDefault(role, tabId) {
  if (String(role || '').toLowerCase() === 'admin') return true;
  // Concerns: a concern may be ABOUT an area manager. Email hub (v131.26):
  // its endpoints are Head-Office-only, so showing the tab to an AM would
  // only show them six cards of errors.
  // JACK (v132.70): Head Office only; the endpoint is gated the same way.
  // Scorecard: Head Office only in Phase 1 (v136.41); from v136.48 (Phase 2) area managers see their own area too.
  return tabId !== 'concerns' && tabId !== 'emailhub' && tabId !== 'jack';
}
function effectiveTabVisible(role, tabId, overrides) {
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, tabId)) return !!overrides[tabId];
  return tabRoleDefault(role, tabId);
}
// Fill the Accounts-edit checklist with each tab's effective visibility.
function renderAccountTabChecks(role, overrides) {
  var box = document.getElementById('acctTabsBox');
  if (!box) return;
  box.innerHTML = TAB_CONTROL.map(function (t) {
    var on = effectiveTabVisible(role, t.id, overrides);
    return '<label style="display:flex;align-items:center;gap:6px;font-size:12px;cursor:pointer;padding:2px 0">' +
      '<input type="checkbox" class="acct-tab-chk" data-tab="' + t.id + '"' + (on ? ' checked' : '') + ' style="width:14px;height:14px;flex:none">' +
      '<span>' + escapeHtml(t.label) + '</span></label>';
  }).join('');
}
// Read the checklist back into an override map — store ONLY tabs that DIFFER from
// the role default, so unset tabs keep following the role default.
function readAccountTabOverrides(role) {
  var overrides = {};
  var nodes = document.querySelectorAll('#acctTabsBox .acct-tab-chk');
  Array.prototype.forEach.call(nodes, function (chk) {
    var tab = chk.getAttribute('data-tab');
    var on = !!chk.checked;
    if (on !== tabRoleDefault(role, tab)) overrides[tab] = on;
  });
  return Object.keys(overrides).length ? overrides : null;
}

function getAreaStoreMap() {
  // v129.14 — the cache key was LENGTH only, so any same-length mutation
  // (editing a store/area, archiving) left a stale map until a full reload —
  // one of the two mechanisms behind the Qatar-AM-four AM invisibility. Key on
  // a cheap content hash of exactly the fields the map reads (~110 rows, so
  // the O(n) key costs far less than one Set build).
  let key = 0;
  if (typeof staffData !== 'undefined' && staffData && staffData.length) {
    for (const s of staffData) {
      const str = ((s && s.area) || '') + '|' + ((s && s.store) || '') + '|' + ((s && s.active === false) ? 0 : 1);
      for (let i = 0; i < str.length; i++) key = ((key * 31) + str.charCodeAt(i)) | 0;
    }
    key = staffData.length + ':' + key;
  }
  if (_areaStoreMapCache && _areaStoreMapCacheKey === key) return _areaStoreMapCache;
  if (typeof staffData === 'undefined' || !staffData || !staffData.length) {
    return AREA_STORE_MAP_FALLBACK;
  }
  const map = {};
  staffData.forEach(s => {
    if (!s || s.active === false || !s.area || !s.store) return;
    if (!map[s.area]) map[s.area] = new Set();
    map[s.area].add(s.store);
  });
  // Convert sets to arrays
  const out = {};
  Object.keys(map).forEach(area => {
    out[area] = Array.from(map[area]).sort();
  });
  _areaStoreMapCache = out;
  _areaStoreMapCacheKey = key;
  return out;
}

async function loadAccountsFromDB() {
  try {
    const rows = await DB.accounts.list();
    if (Array.isArray(rows)) {
      // saveAccounts() writes to localStorage.db_accounts so subsequent
      // sync loadAccounts() calls return this list.
      saveAccounts(rows);
      _accounts = rows;
      dlog('[DB] Loaded ' + rows.length + ' accounts from database');
    }
  } catch(e) {
    console.warn('[DB] Accounts load failed, using cached value:', e.message);
  }
}

function getDefaultAreaId() {
  return (ALL_AREAS && ALL_AREAS.length) ? ALL_AREAS[0].id : '';
}

function getActiveAreaManagerName(areaId) {
  if (!areaId) return 'Unassigned';
  try {
    const accounts = loadAccounts();
    if (!Array.isArray(accounts)) return 'Unassigned';
    // v132.71: an area can carry two managers during a handover; name both.
    const names = accounts.filter(a =>
      a && a.role === 'area_manager' && a.active !== false && String(a.area) === String(areaId) && a.name
    ).map(a => a.name).sort();
    return names.length ? names.join(' & ') : 'Unassigned';
  } catch (e) {
    return 'Unassigned';
  }
}

function getAreaNameById(areaId) {
  if (areaId === null || areaId === undefined || areaId === '') return '';
  const found = (ALL_AREAS || []).find(a => String(a.id) === String(areaId));
  return found ? found.name : String(areaId);
}

function populateAreaSelects() {
  // Format: "UAE — <Manager Name>" or "UAE — Unassigned"
  const label = a => {
    const mgr = getActiveAreaManagerName(a.id);
    return a.name + ' — ' + mgr;
  };
  const optionsHtml = ALL_AREAS.map(a =>
    '<option value="' + escapeHtml(a.id) + '">' + escapeHtml(label(a)) + '</option>'
  ).join('');

  // Each dropdown spec: id, optional prefix HTML (placeholder), optional suffix HTML
  const dropdowns = [
    { id: 'sfArea',           prefix: '' },
    { id: 'newSfArea',        prefix: '' },
    { id: 'filterArea',       prefix: '<option value="">All Areas</option>' },
    { id: 'smStoreArea',      prefix: '', suffix: '<option value="">None / Virtual</option>' },
    { id: 'toilOverviewArea', prefix: '<option value="">All Areas</option>' },
    { id: 'acctArea',         prefix: '' },
  ];

  dropdowns.forEach(d => {
    const el = document.getElementById(d.id);
    if (!el) return;
    // Preserve current selection if possible
    const prevValue = el.value;
    el.innerHTML = (d.prefix || '') + optionsHtml + (d.suffix || '');
    // Restore selection if it's still valid
    if (prevValue && Array.from(el.options).some(o => o.value === prevValue)) {
      el.value = prevValue;
    }
  });
}

function loadAccounts() {
  // Sync read from cache — DB load happens via loadAccountsFromDB() on init.
  try {
    const saved = localStorage.getItem('db_accounts');
    if (saved) return JSON.parse(saved);
    const legacy = localStorage.getItem('elc_accounts');
    if (legacy) return JSON.parse(legacy);
  } catch(e) {}
  return [];
}

function saveAccounts(accounts) {
  localStorage.setItem('db_accounts', JSON.stringify(accounts));
  // No single-record write — bulk save handled by callers via DB.accounts.*
}

// ── v129.108 — "this address rejected our mail" ──────────────────────────────
// Matt, 11 Aug: three Area Managers' addresses were wrong, so their Monday
// briefings went nowhere for weeks. The bounce ledger (v193-0) records it, but a
// log nobody opens is not a guard — the warning has to appear where the fix is
// made, which is this row.
//
// Deliberately silent when the ledger is not live: an environment without v193-0
// must look exactly as it does today, not sprout empty warnings.
var _bounceByAddress = {};
var _bounceFlagsTried = false;   // one lookup per page load, not per repaint

function bounceFlagHtml(email) {
  var addr = String(email || '').trim().toLowerCase();
  if (!addr) return '';
  var b = _bounceByAddress[addr];
  if (!b) return '';
  var when = String(b.last_at || '').slice(0, 10);
  var why = b.status === 'complained'
    ? 'marked our mail as spam'
    : 'rejected our mail' + (b.reason ? ' — ' + b.reason : '');
  return '<div style="margin-top:3px;font-size:11px;font-weight:700;color:var(--ui-warn)" ' +
    'title="' + escapeHtml(why) + '">⚠️ This address ' +
    (b.status === 'complained' ? 'marked our mail as spam' : 'rejected our mail') +
    (when ? ' (' + escapeHtml(when) + ')' : '') + ' — check it is correct</div>';
}

// Load once per Accounts render. Never throws and never blocks the table: a
// failed lookup just means no flags, same as today.
async function loadBounceFlags() {
  try {
    var res = await DB.email.bounces();
    _bounceByAddress = {};
    if (!res || res.live === false) return;
    (res.addresses || []).forEach(function (a) {
      if (a && a.address) _bounceByAddress[String(a.address).toLowerCase()] = a;
    });
  } catch (e) { _bounceByAddress = {}; }
}

function renderManagers() {
  const tbody = document.getElementById('managersBody');
  if (!tbody) return;
  _accounts = loadAccounts(); // always read fresh
  const roleLabel = {admin:'Head Office', area_manager:'Area Manager', acting:'Acting area manager'};   // v133.61
  const roleBadge = {admin:'badge-green', area_manager:'badge-blue', acting:'badge-amber'};

  if (!_accounts.length) {
    tbody.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:24px;color:var(--sub)">No accounts yet — tap + Add Account to create one.</td></tr>';
    return;
  }

  // v129.108 — fetch the bounce flags once, then repaint. Fire-and-forget so the
  // table never waits on it: the flags are an annotation, not the data.
  if (!_bounceFlagsTried) {
    _bounceFlagsTried = true;
    loadBounceFlags().then(function () {
      if (Object.keys(_bounceByAddress).length) renderManagers();
    });
  }

  tbody.innerHTML = _accounts.map((m, i) => `<tr>
    <td translate="no"><strong>${escapeHtml(m.name||'')}</strong></td>
    <td style="font-size:12px;color:var(--sub)"><span translate="no">${escapeHtml(m.email||'')}</span>${bounceFlagHtml(m.email)}</td>
    <td><span class="badge ${roleBadge[m.role]||'badge-grey'}">${roleLabel[m.role]||m.role}</span></td>
    <td style="font-size:12px">${m.role==='area_manager' ? '<span translate="no">' + escapeHtml(getAreaNameById(m.area)||'—') + '</span> area' : m.role==='acting' ? 'The area on their acting dates' : m.area ? '<span translate="no">' + escapeHtml(m.area) + '</span>' : 'All stores'}</td>
    <td style="white-space:nowrap">
      <button class="btn btn-outline btn-sm" onclick="openEditAccountModal(${i})">Edit</button>
      <button class="btn btn-outline btn-sm" onclick="changeAccountPassword(${i})">🔑 Password</button>
      ${viewerIsHeadOffice() ? `<button class="btn btn-outline btn-sm" title="Unlock sign-in after too many wrong passwords" onclick="unlockAccountLogin(${i})">🔓 Unlock</button>` : ''}
      <button class="btn btn-outline btn-sm" style="color:var(--red);border-color:var(--red);margin-inline-start:4px" onclick="deleteAccount(${i})">Remove</button>
    </td>
  </tr>`).join('');
}

// v129.19 — offer the departments already in use so spellings converge without
// being enforced (the field stays free text: Matt can invent a department
// without a release). Reads the accounts already loaded — no extra request.
function fillDepartmentList() {
  var dl = document.getElementById('acctDeptList');
  if (!dl) return;
  var seen = {};
  var names = [];
  (loadAccounts() || []).forEach(function (a) {
    var d = (a && a.department ? String(a.department) : '').trim();
    if (!d) return;
    var key = d.toLowerCase();
    if (seen[key]) return;
    seen[key] = true;
    names.push(d);
  });
  names.sort();
  dl.innerHTML = names.map(function (d) {
    return '<option value="' + (typeof escapeHtml === 'function' ? escapeHtml(d) : d) + '"></option>';
  }).join('');
}

function openAddAccountModal() {
  document.getElementById('accountModalTitle').textContent = 'Add Account';
  document.getElementById('acctName').value  = '';
  document.getElementById('acctEmail').value = '';
  document.getElementById('acctRole').value  = 'admin';
  document.getElementById('acctNote').value  = '';
  var _dp = document.getElementById('acctDepartment'); if (_dp) _dp.value = '';
  var _bc = document.getElementById('acctBriefingCc'); if (_bc) _bc.checked = false;
  fillDepartmentList();
  var _sp = document.getElementById('acctShowPlanner'); if (_sp) _sp.checked = false;
  document.getElementById('acctEditIdx').value = '';
  fillActingStaff(null);   // v133.61
  fillOrgFields(null);     // v136.86: the org chart fields
  if (!_org) loadOrgChart().then(function () { fillOrgFields(null); });
  updateAccountScopeField();
  renderAccountTabChecks('admin', null);   // new account → role defaults
  document.getElementById('accountModal').classList.add('open');
}

function openEditAccountModal(idx) {
  const accounts = loadAccounts();
  const m = accounts[idx];
  if (!m) return;
  document.getElementById('accountModalTitle').textContent = 'Edit Account';
  document.getElementById('acctName').value  = m.name;
  document.getElementById('acctEmail').value = m.email;
  document.getElementById('acctRole').value  = m.role;
  document.getElementById('acctNote').value  = m.area || '';
  var _dpE = document.getElementById('acctDepartment'); if (_dpE) _dpE.value = m.department || '';
  var _bcE = document.getElementById('acctBriefingCc'); if (_bcE) _bcE.checked = !!m.briefing_cc;
  fillDepartmentList();
  var _sp = document.getElementById('acctShowPlanner'); if (_sp) _sp.checked = !!m.show_in_planner;
  document.getElementById('acctEditIdx').value = String(idx);
  fillActingStaff(m.staff_id);   // v133.61
  fillOrgFields(m.id);           // v136.86: the org chart fields, from the chart read
  if (!_org) loadOrgChart().then(function () { fillOrgFields(m.id); });
  updateAccountScopeField();
  if (m.role === 'area_manager') {
    document.getElementById('acctArea').value = m.area || getDefaultAreaId();
  }
  renderAccountTabChecks(m.role, m.tab_overrides || null);   // load this person's tab visibility (after scope reset)
  document.getElementById('accountModal').classList.add('open');
}

function closeAccountModal() {
  document.getElementById('accountModal').classList.remove('open');
}

// v133.61: the people an acting account can be for (active staff, by store then name)
function fillActingStaff(selectedId) {
  var sel = document.getElementById('acctStaff');
  if (!sel) return;
  var people = (typeof staffData !== 'undefined' && Array.isArray(staffData) ? staffData : []).filter(function (s) { return s && s.active !== false && s.name; })
    .slice().sort(function (a, b) { return String(a.store).localeCompare(String(b.store)) || String(a.name).localeCompare(String(b.name)); });
  sel.innerHTML = '<option value="">Choose the person</option>' + people.map(function (s) {
    return '<option value="' + Number(s.id) + '"' + (Number(selectedId) === Number(s.id) ? ' selected' : '') + '>' + escapeHtml(s.name + ' (' + (s.store || '') + ')') + '</option>';
  }).join('');
}
function updateAccountScopeField() {
  const role = document.getElementById('acctRole').value;
  document.getElementById('acctAreaGroup').style.display = role === 'area_manager' ? 'block' : 'none';
  var _sg = document.getElementById('acctStaffGroup'); if (_sg) _sg.style.display = role === 'acting' ? 'block' : 'none';   // v133.61
  document.getElementById('acctNoteGroup').style.display = role === 'admin' ? 'block' : 'none';
  // When the role is changed via the dropdown, reset the tab checklist to that
  // role's defaults (openEdit re-applies the saved overrides afterwards).
  renderAccountTabChecks(role, null);
}

function saveAccount() {
  const name     = document.getElementById('acctName').value.trim();
  const email    = document.getElementById('acctEmail').value.trim();
  const role     = document.getElementById('acctRole').value;
  const area     = document.getElementById('acctArea').value;
  const note     = document.getElementById('acctNote').value.trim();
  const deptEl   = document.getElementById('acctDepartment');
  const department = deptEl ? deptEl.value.trim() : '';
  const editIdx  = document.getElementById('acctEditIdx').value;

  if (!name)  { showToast('Enter a name', 'error'); return; }
  if (!email) { showToast('Enter an email or login ID', 'error'); return; }
  // v133.61: an acting account must name its person
  var actingStaff = role === 'acting' ? Number((document.getElementById('acctStaff') || {}).value || 0) : 0;
  if (role === 'acting' && !actingStaff) { showToast('Choose who is acting', 'error'); return; }

  const spEl = document.getElementById('acctShowPlanner');
  const accounts = loadAccounts();
  const record = {
    name, email, role,
    area: role === 'area_manager' ? area : role === 'acting' ? '' : note,
    // v129.19 — the person's function/team. Empty string normalises to null so
    // a blank field clears the department rather than creating a "" group.
    department: department || null,
    // v129.24 — CC'd on the weekly compliance briefing (any role).
    briefing_cc: (function(){ var el = document.getElementById('acctBriefingCc'); return el ? !!el.checked : false; })(),
    show_in_planner: spEl ? !!spEl.checked : false,
    tab_overrides: readAccountTabOverrides(role),   // per-person admin tab visibility (null = role defaults)
  };
  if (role === 'acting') record.staff_id = actingStaff;   // v133.61
  Object.assign(record, orgFieldsForSave());               // v136.86: job title + reports to (Head Office only)
  _org = null;                                             // v136.86: the chart re-reads after a save

  if (editIdx !== '') {
    const old = accounts[parseInt(editIdx)];
    accounts[parseInt(editIdx)] = record;
    // Update in DB if record has an id
    if (old && old.id) {
      record.id = old.id;
      // v133.101: "Account updated" waits for the save. It used to show at once, then a red failure toast
      // arrived after it, and an account edit that was refused (8 Oct 2026, an area manager handover) read as
      // done. Now: the save's own answer, including WHY it was refused.
      DB.accounts.update(old.id, record).then(r => {
        // v130.44 — the Worker now saves the account even on a database that
        // predates an optional column (department/briefing_cc), and says which
        // field it had to drop. Toasting "Account updated" over that would be
        // the silent lie this app keeps removing: the field is NOT set, and the
        // person would find out weeks later when the briefing does not CC them.
        if (r && r.note) showToast('⚠️ ' + r.note, 'error');
        else showToast('Account updated', 'success');
      }).catch(e => {
        console.error('[DB] Account update failed:', e.message);
        showToast('⚠️ Account NOT saved: ' + (e.message || 'the save failed') + '. Reload to see what is saved, then try again.', 'error');
      });
    } else {
      showToast('Account updated on this device only (it has no database id yet)', 'error');
    }
  } else {
    accounts.push(record);
    DB.accounts.create(record).then(r => {
      // The response is the PostgREST rows array normally, and { data, note }
      // when an optional column had to be dropped — read the id out of either.
      // (The old `r.id` read never fired: PostgREST returns an ARRAY here.)
      const row = Array.isArray(r) ? r[0] : (r && Array.isArray(r.data) ? r.data[0] : r);
      if (row && row.id) record.id = row.id;
      if (r && r.note) showToast('⚠️ ' + r.note, 'error');
    })
      .catch(e => {
        console.error('[DB] Account create failed:', e.message);
        showToast('⚠️ Account create failed in DB — user cannot log in until retried', 'error');
      });
    showToast(name + ' added — they can log in via the admin panel', 'success');
  }

  saveAccounts(accounts);
  _accounts = accounts;
  closeAccountModal();
  renderManagers();
}

async function deleteAccount(idx) {
  const accounts = loadAccounts();
  const m = accounts[idx];
  if (!m) return;
  if (!(await (window.uiConfirm || function (m) { return Promise.resolve(confirm(m)); })('Remove ' + m.name + '?\nThey will no longer be able to log into the admin panel.', { danger: true, confirmLabel: 'Remove' }))) return;
  if (m.id) DB.accounts.remove(m.id).catch(e => {
    console.error('[DB] Account delete failed:', e.message);
    showToast('⚠️ Account delete failed in DB — may still be able to log in', 'error');
  });
  accounts.splice(idx, 1);
  saveAccounts(accounts);
  _accounts = accounts;
  renderManagers();
  showToast(m.name + ' removed', 'success');
}

async function changeAccountPassword(idx) {
  const accounts = loadAccounts();
  const m = accounts[idx];
  if (!m || !m.id) { showToast('Account not found or not saved to DB', 'error'); return; }
  // v133.101: 8, the Worker's own minimum (login.js). The box used to say 4, so a 4-7 character password
  // passed here, the Worker refused it, and the person could not sign in as themselves ("Invalid
  // credentials", Qatar, 8 Oct 2026).
  // v133.120: a Shift pop-up that hides what is typed (the browser's prompt() box cannot)
  const newPw = await askSecret({ title: 'Set a new password for ' + m.name, note: 'At least 8 characters. Tell them in person or by phone.', minLength: 8 });
  if (!newPw) return;
  if (newPw.length < 8) { showToast('Password must be at least 8 characters', 'error'); return; }
  try {
    await DB.auth.changePassword(m.id, newPw);
    showToast('Password updated for ' + m.name, 'success');
  } catch(e) {
    showToast('Failed: ' + (e.message || 'unknown error'), 'error');
  }
}

// v136.9 (Matt, 17 Oct 2026): an account locks after 10 wrong passwords in 15 minutes; Head Office
// can end that early. The Worker records the unlock (auth_events + the audit log) and refuses anyone
// else. admin.html declares currentAdmin with let: a bare global here, not window.currentAdmin.
function viewerIsHeadOffice() {
  try {
    var who = (typeof currentAdmin !== 'undefined') ? currentAdmin : null;
    return !!(who && String(who.role || '').toLowerCase() === 'admin');
  } catch (e) { return false; }
}
async function unlockAccountLogin(idx) {
  const m = loadAccounts()[idx];
  if (!m || !m.id) { showToast('Account not found or not saved to DB', 'error'); return; }
  try {
    const r = await DB.auth.unlockLogin({ account_id: m.id });
    showToast(r && r.was_locked ? 'Unlocked. They can sign in again now.' : 'That sign-in was not locked.', 'success');
  } catch (e) {
    showToast('Unlock failed: ' + ((e && e.message) || 'unknown error'), 'error');
  }
}

function getAreaStoreCount(areaId) {
  if (typeof storesData === 'undefined' || !Array.isArray(storesData)) {
    // Fallback: derive store→area from staffData if storesData not loaded
    if (typeof staffData === 'undefined' || !Array.isArray(staffData)) return 0;
    const seen = new Set();
    staffData.forEach(s => { if (String(s.area) === String(areaId) && s.store) seen.add(s.store); });
    return seen.size;
  }
  return storesData.filter(s => String(s.area_id) === String(areaId)).length;
}

function getAreaStaffCount(areaId) {
  if (typeof staffData === 'undefined' || !Array.isArray(staffData)) return 0;
  return staffData.filter(s => String(s.area) === String(areaId) && s.active !== false).length;
}

function getActiveAreaManagerAccount(areaId) {
  if (!areaId) return null;
  try {
    const accounts = loadAccounts();
    if (!Array.isArray(accounts)) return null;
    return accounts.find(a =>
      a && a.role === 'area_manager' && a.active !== false && String(a.area) === String(areaId)
    ) || null;
  } catch (e) { return null; }
}

function suggestNextAreaId() {
  const numericIds = (ALL_AREAS || [])
    .map(a => parseInt(a.id, 10))
    .filter(n => !isNaN(n));
  const next = numericIds.length ? Math.max(...numericIds) + 1 : 1;
  return String(next);
}

function buildAreaManagerOptions(currentMgr) {
  const accounts = loadAccounts() || [];
  const eligible = accounts.filter(a =>
    a && a.role === 'area_manager' && a.active !== false
  );
  // De-dup against currentMgr by name (currentMgr is already in eligible if active)
  const others = eligible.filter(a => !currentMgr || a.name !== currentMgr.name);

  let html = '<option value="">— Unassigned —</option>';
  if (currentMgr) {
    html += '<option value="' + escapeHtml(currentMgr.name) + '" selected>'
          + escapeHtml(currentMgr.name) + ' (current)</option>';
  }
  others.forEach(a => {
    html += '<option value="' + escapeHtml(a.name) + '">'
          + escapeHtml(a.name) + '</option>';
  });
  return html;
}

function openAddAreaModal() {
  document.getElementById('areaModalTitle').textContent = 'Add Area';
  document.getElementById('areaId').value = suggestNextAreaId();
  document.getElementById('areaId').readOnly = false;
  document.getElementById('areaName').value = '';
  document.getElementById('areaRegion').value = '';
  document.getElementById('areaCountry').value = 'ELC - UAE';
  document.getElementById('areaEditId').value = '';
  document.getElementById('areaPrevManagerName').value = '';
  // Manager assignment is deferred for Add Area (you'd assign after the
  // area exists — otherwise the form races against the area's row not yet
  // being in the DB when we PATCH the manager's account).
  document.getElementById('areaManagerGroup').style.display = 'none';
  document.getElementById('areaModal').classList.add('open');
  document.getElementById('areaName').focus();
}

function openEditAreaModal(id) {
  const a = (ALL_AREAS || []).find(x => String(x.id) === String(id));
  if (!a) { showToast('Area not found', 'error'); return; }
  document.getElementById('areaModalTitle').textContent = 'Edit Area: ' + a.name;
  document.getElementById('areaId').value = a.id;
  document.getElementById('areaId').readOnly = true;  // can't edit id (would cascade FKs)
  document.getElementById('areaName').value = a.name || '';
  document.getElementById('areaRegion').value = a.region || '';
  document.getElementById('areaCountry').value = a.country || 'ELC - UAE';
  document.getElementById('areaEditId').value = a.id;
  // Manager dropdown
  const currentMgr = getActiveAreaManagerAccount(a.id);
  document.getElementById('areaPrevManagerName').value = currentMgr ? currentMgr.name : '';
  setHTML('areaManagerSelect', buildAreaManagerOptions(currentMgr));   // v136.8: null-safe (the box may not be on screen)
  document.getElementById('areaManagerGroup').style.display = 'block';
  document.getElementById('areaModal').classList.add('open');
}

function closeAreaModal() {
  document.getElementById('areaModal').classList.remove('open');
}

// ── re-expose (preserves the pre-extraction global surface) ──
// ── v136.86: the org chart ───────────────────────────────────────────────────
// Matt, 1 Nov 2026: "lets add title and who they report to in shift. that can then auto create a hierarchy?"
// Each account's job_title and reports_to (migration v246-0) come from /api/admin/org-chart, which also builds
// the tree, so the page never holds a second copy of the rules. _org is null until the first read.
var _org = null;   // { ready, people: [{ id, name, job_title, reports_to, ... }], tree }

async function loadOrgChart() {
  try {
    var r = await DB.accounts.orgChart();
    _org = (r && r.ok) ? r : null;
  } catch (e) { _org = null; }
  return _org;
}
function orgPerson(id) {
  if (!_org || !Array.isArray(_org.people) || id == null) return null;
  for (var i = 0; i < _org.people.length; i++) if (String(_org.people[i].id) === String(id)) return _org.people[i];
  return null;
}
function isHeadOfficeAdmin() {
  return typeof currentAdmin !== 'undefined' && currentAdmin && String(currentAdmin.role || '').toLowerCase() === 'admin';
}
// The two form fields: filled from the chart read, hidden from anyone who cannot set them.
function fillOrgFields(selfId) {
  var group = document.getElementById('acctOrgGroup');
  var titleEl = document.getElementById('acctJobTitle');
  var sel = document.getElementById('acctReportsTo');
  if (!group || !titleEl || !sel) return;
  group.style.display = isHeadOfficeAdmin() ? '' : 'none';
  var me = orgPerson(selfId);
  titleEl.value = (me && me.job_title) || '';
  var current = me && me.reports_to != null ? String(me.reports_to) : '';
  var people = (loadAccounts() || []).filter(function (a) { return a && a.id != null && a.active !== false && String(a.id) !== String(selfId); })
    .slice().sort(function (a, b) { return String(a.name).localeCompare(String(b.name)); });
  sel.innerHTML = '<option value="">No one (top of the chart)</option>' + people.map(function (a) {
    var p = orgPerson(a.id);
    var label = a.name + (p && p.job_title ? ', ' + p.job_title : '');
    return '<option value="' + Number(a.id) + '"' + (String(a.id) === current ? ' selected' : '') + ' translate="no">' + escapeHtml(label) + '</option>';
  }).join('');
}
// What saveAccount sends: only for Head Office, and only once the chart has been read (so an old database
// is not asked to save two columns it does not have on every account edit).
function orgFieldsForSave() {
  if (!isHeadOfficeAdmin() || !_org || !_org.ready) return {};
  var titleEl = document.getElementById('acctJobTitle');
  var sel = document.getElementById('acctReportsTo');
  if (!titleEl || !sel) return {};
  return { job_title: titleEl.value.trim() || null, reports_to: sel.value ? Number(sel.value) : null };
}

function orgNodeHtml(n) {
  var face = (typeof Avatars !== 'undefined' && Avatars.chip) ? Avatars.chip('account', n.id, n.name, 30) + ' ' : '';
  var sub = [n.job_title, n.department].filter(Boolean).map(escapeHtml).join(' &middot; ');
  return '<li style="margin:6px 0;list-style:none">' +
    '<div style="display:inline-flex;align-items:center;gap:8px;padding:6px 10px;border:1px solid var(--border);border-radius:10px;background:var(--card)">' + face +
      '<div><div style="font-weight:700;font-size:13px" translate="no">' + escapeHtml(n.name) + '</div>' +
      (sub ? '<div style="font-size:11.5px;color:var(--sub)">' + sub + '</div>' : '<div style="font-size:11.5px;color:var(--tiny)">No job title yet</div>') +
    '</div></div>' +
    (n.children && n.children.length ? '<ul style="margin:0;padding-inline-start:26px;border-inline-start:2px solid var(--border)">' + n.children.map(orgNodeHtml).join('') + '</ul>' : '') +
  '</li>';
}
async function openOrgChart() {
  var ov = document.getElementById('orgChartOverlay');
  if (!ov) {
    ov = document.createElement('div');
    ov.id = 'orgChartOverlay';
    ov.className = 'modal-overlay open';
    ov.innerHTML = '<div class="modal" style="max-width:720px;width:94vw;max-height:86vh;overflow:auto">' +
      '<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:8px"><div style="font-weight:800;font-size:16px">🏢 Org chart</div>' +
      '<button class="btn btn-outline btn-sm" onclick="closeOrgChart()">Close</button></div>' +
      '<div id="orgChartBody" style="font-size:13px">Loading…</div></div>';
    ov.addEventListener('click', function (ev) { if (ev.target === ov) closeOrgChart(); });
    document.body.appendChild(ov);
  }
  ov.classList.add('open');
  var body = document.getElementById('orgChartBody');
  var org = await loadOrgChart();
  if (!body) return;
  if (!org) { body.textContent = 'Could not load the org chart. Check the connection and try again.'; return; }
  if (!org.ready) { body.textContent = 'The org chart needs a database update first (migrations/v246-0-accounts-org-chart.sql).'; return; }
  var placed = (org.people || []).filter(function (p) { return p.job_title || p.reports_to != null; }).length;
  body.innerHTML = '<div style="color:var(--sub);margin-bottom:10px">Built from each account\'s job title and who they report to. Head Office sets both on the Accounts page (Edit).' +
    (placed ? '' : ' Nobody has been placed yet, so everyone is listed at the top.') + '</div>' +
    '<ul style="margin:0;padding:0">' + (org.tree || []).map(orgNodeHtml).join('') + '</ul>';
}
function closeOrgChart() {
  var ov = document.getElementById('orgChartOverlay');
  if (ov) ov.classList.remove('open');
}
window.openOrgChart = openOrgChart;
window.closeOrgChart = closeOrgChart;

window.TAB_CONTROL = TAB_CONTROL;
window.tabRoleDefault = tabRoleDefault;
window.effectiveTabVisible = effectiveTabVisible;
window.renderAccountTabChecks = renderAccountTabChecks;
window.getAreaStoreMap = getAreaStoreMap;
window.loadAccountsFromDB = loadAccountsFromDB;
window.getDefaultAreaId = getDefaultAreaId;
window.getActiveAreaManagerName = getActiveAreaManagerName;
window.getAreaNameById = getAreaNameById;
window.populateAreaSelects = populateAreaSelects;
window.loadAccounts = loadAccounts;
window.saveAccounts = saveAccounts;
window.renderManagers = renderManagers;
// v131.49 - the Head-Office order (post-Cubes): every account must set a new
// password at its next login and every other session is signed out now.
async function requireResetAll() {
  const typed = prompt('This signs EVERY Head Office and area manager out now (except you) and makes each of them set a NEW password the next time they log in - on the admin app or the staff app.\n\nType RESET to continue.');
  if (typed !== 'RESET') return;
  try {
    const r = await DB.accounts.requireResetAll();
    showToast((r && r.flagged != null ? r.flagged : '?') + ' accounts must set a new password at next login; everyone else is signed out', 'success');
  } catch (e) { showToast('Failed: ' + ((e && e.message) || 'unknown'), 'error'); }
}
window.requireResetAll = requireResetAll;
window.openAddAccountModal = openAddAccountModal;
window.openEditAccountModal = openEditAccountModal;
window.closeAccountModal = closeAccountModal;
window.updateAccountScopeField = updateAccountScopeField;
window.saveAccount = saveAccount;
window.deleteAccount = deleteAccount;
window.changeAccountPassword = changeAccountPassword;
window.unlockAccountLogin = unlockAccountLogin;   // v136.9
window.getAreaStoreCount = getAreaStoreCount;
window.getAreaStaffCount = getAreaStaffCount;
window.getActiveAreaManagerAccount = getActiveAreaManagerAccount;
window.suggestNextAreaId = suggestNextAreaId;
window.buildAreaManagerOptions = buildAreaManagerOptions;
window.openAddAreaModal = openAddAreaModal;
window.openEditAreaModal = openEditAreaModal;
window.closeAreaModal = closeAreaModal;

})();
